import { useState } from 'react';
import { comments, ranking, topicTags } from '@/mocks/home';

const avatarClasses: Record<string, string> = {
  primary: 'bg-primary-100 text-primary-700 border-primary-200',
  accent: 'bg-accent-100 text-accent-700 border-accent-200',
  secondary: 'bg-secondary-100 text-secondary-700 border-secondary-200',
};

const trendIcon: Record<string, string> = {
  up: 'ri-arrow-up-line text-primary-600',
  flat: 'ri-subtract-line text-foreground-400',
  down: 'ri-arrow-down-line text-secondary-500',
};

export default function CommentsSection() {
  const [liked, setLiked] = useState<Record<string, boolean>>({ c2: true });
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState('');

  const toggleLike = (id: string) => setLiked((prev) => ({ ...prev, [id]: !prev[id] }));

  const handlePost = () => {
    if (!draft.trim()) {
      setNotice('先写两句再发吧，空着可进不了评论区。');
      return;
    }
    setNotice('已记录在你的本地草稿里，正式提交稍后开放。');
    setDraft('');
  };

  return (
    <section id="comments" className="w-full bg-background-50 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-2 text-xs font-label tracking-[0.24em] text-primary-600">
                  <span className="w-6 h-px bg-primary-400" />
                  DISCUSSION
                </span>
                <h2 className="mt-4 font-heading font-black text-foreground-950 text-3xl md:text-4xl tracking-tight">
                  评论区
                </h2>
              </div>
              <div className="flex items-center gap-2 text-xs font-label text-foreground-500">
                <i className="ri-chat-smile-2-line text-base text-accent-500" />
                共 3,642 条讨论 · 处理中
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-background-200/80 bg-background-100/60 p-4 md:p-5">
              <div className="flex gap-3">
                <span className="w-10 h-10 rounded-full bg-secondary-100 text-secondary-700 border border-secondary-200 flex items-center justify-center shrink-0 font-label text-sm">
                  我
                </span>
                <div className="flex-1">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value.slice(0, 500))}
                    rows={3}
                    placeholder="说说你对灵灵黑料深扒细节里的哪一段印象最深…"
                    className="w-full resize-none rounded-md bg-background-50 border border-background-200 px-3.5 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  />
                  <div className="mt-2.5 flex items-center justify-between gap-3">
                    <span className="text-xs font-label text-foreground-400">{draft.length}/500</span>
                    <button
                      type="button"
                      onClick={handlePost}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-send-plane-fill text-xs" />
                      发布讨论
                    </button>
                  </div>
                  {notice && (
                    <p className="mt-2 text-xs font-label text-accent-600">{notice}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-col divide-y divide-background-200/80">
              {comments.map((c) => (
                <article key={c.id} className="py-5">
                  <div className="flex gap-3.5">
                    <span
                      className={`w-10 h-10 rounded-full border flex items-center justify-center shrink-0 font-heading font-bold text-sm ${
                        avatarClasses[c.avatarColor]
                      }`}
                    >
                      {c.name.slice(0, 1)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-label font-medium text-foreground-950 text-sm">
                          {c.name}
                        </span>
                        {c.pinned && (
                          <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[11px] font-label">
                            作者精选
                          </span>
                        )}
                        <span className="text-xs font-label text-foreground-400">{c.time}</span>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-700">{c.content}</p>
                      <div className="mt-3 flex items-center gap-5">
                        <button
                          type="button"
                          onClick={() => toggleLike(c.id)}
                          className={`inline-flex items-center gap-1.5 text-xs font-label transition-colors ${
                            liked[c.id] ? 'text-primary-600' : 'text-foreground-500 hover:text-primary-600'
                          }`}
                        >
                          <i className={liked[c.id] ? 'ri-heart-3-fill' : 'ri-heart-3-line'} />
                          {c.likes + (liked[c.id] ? 1 : 0)}
                        </button>
                        <span className="inline-flex items-center gap-1.5 text-xs font-label text-foreground-500">
                          <i className="ri-chat-1-line" />
                          {c.replies} 条回复
                        </span>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 text-xs font-label text-foreground-500 hover:text-primary-600 transition-colors"
                        >
                          <i className="ri-share-forward-line" />
                          转存
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="rounded-lg border border-background-200/80 bg-background-100/60 p-5 md:p-6">
                <div className="flex items-center gap-2">
                  <i className="ri-bar-chart-2-line text-accent-600 text-lg" />
                  <h3 className="font-heading font-bold text-foreground-950 text-lg">话题热度榜</h3>
                </div>
                <ol className="mt-5 flex flex-col divide-y divide-background-200/70">
                  {ranking.map((r, i) => (
                    <li key={r.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                      <span
                        className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-label shrink-0 ${
                          i < 3
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-200 text-foreground-600'
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="flex-1 text-sm font-label text-foreground-800 truncate">
                        {r.label}
                      </span>
                      <span className="text-xs font-label text-foreground-500 shrink-0">{r.count}</span>
                      <i className={`${trendIcon[r.trend]} text-sm shrink-0`} />
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg border border-background-200/80 bg-background-100/60 p-5 md:p-6">
                <div className="flex items-center gap-2">
                  <i className="ri-price-tag-3-line text-secondary-600 text-lg" />
                  <h3 className="font-heading font-bold text-foreground-950 text-lg">相关标签</h3>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {topicTags.slice(0, 10).map((t) => (
                    <a
                      key={t.id}
                      href="#topics"
                      className="px-2.5 py-1.5 rounded-md bg-background-50 border border-background-200 text-xs font-label text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
                    >
                      #{t.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}